/**
 * Google Apps Script Web App client for the LUMORA storefront.
 */
import { GAS_URL } from '../config/apiConfig';
import { readProductCache, writeProductCache } from '../utils/productCache';

const CACHE_TTL_MS = 2 * 60_000;
const STALE_TTL_MS = 24 * 60 * 60_000;
const MAX_ATTEMPTS = 4;
const PRODUCT_FETCH_ATTEMPTS = 2;

class GasRetryError extends Error {
  constructor(delay) {
    super('Retry GAS request');
    this.name = 'GasRetryError';
    this.retry = true;
    this.delay = delay;
  }
}

const memoryCache = { data: null, at: 0 };
let inflight = null;
let inflightFresh = null;

function seedMemoryFromStorage() {
  if (memoryCache.data) return;
  const stored = readProductCache({ allowStale: true });
  if (stored) {
    memoryCache.data = stored;
    memoryCache.at = Date.now();
  }
}

function getStaleProducts() {
  seedMemoryFromStorage();
  if (!memoryCache.data) return null;
  if (Date.now() - memoryCache.at > STALE_TTL_MS) return null;
  return memoryCache.data;
}

function storeProductCache(result) {
  memoryCache.data = result;
  memoryCache.at = Date.now();
  writeProductCache(result);
}

function normalizeCreateOrderResult(raw) {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid order response from server. Please try again.');
  }

  let payload = raw;
  if (payload.data !== undefined && payload.data !== null && !payload.orderId && !payload.id) {
    payload = typeof payload.data === 'string'
      ? JSON.parse(payload.data)
      : payload.data;
  }

  const orderId = payload.orderId ?? payload.id ?? payload.order_id;
  if (orderId === undefined || orderId === null || orderId === '') {
    throw new Error(
      'Order could not be confirmed — no order number returned. Please contact us on WhatsApp with your name and phone.'
    );
  }

  const normalizedId = Number(String(orderId).replace(/\D/g, '')) || orderId;
  return { ...payload, orderId: normalizedId, id: normalizedId };
}

function parseGasResponse(text, attempt, action, maxAttempts = MAX_ATTEMPTS) {
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    const preview = text.replace(/\s+/g, ' ').trim().slice(0, 120);
    const isHtml = preview.startsWith('<!DOCTYPE') || preview.startsWith('<html');
    if (isHtml && attempt < maxAttempts) {
      throw new GasRetryError(400 * attempt);
    }
    const stale = getStaleProducts();
    if (action === 'getProducts' && stale) return stale;
    throw new Error(
      isHtml
        ? 'Store temporarily busy — please wait and refresh.'
        : `Invalid response from Google Apps Script (${preview || 'empty'})`
    );
  }

  if (data.error) {
    const stale = getStaleProducts();
    if (action === 'getProducts' && stale) return stale;
    throw new Error(data.error);
  }

  return data.data !== undefined ? data.data : data;
}

async function gasGetRequest(action, params = {}, attempt = 1, maxAttempts = MAX_ATTEMPTS) {
  const qs = new URLSearchParams({ action, ...params });
  const url = `${GAS_URL}?${qs.toString()}`;

  let res;
  try {
    res = await fetch(url, { method: 'GET', mode: 'cors', redirect: 'follow' });
  } catch (networkErr) {
    if (attempt < maxAttempts) {
      await new Promise((r) => setTimeout(r, 500 * attempt));
      return gasGetRequest(action, params, attempt + 1, maxAttempts);
    }
    throw networkErr;
  }

  const text = await res.text();
  try {
    return parseGasResponse(text, attempt, action, maxAttempts);
  } catch (err) {
    if (err?.retry) {
      await new Promise((r) => setTimeout(r, err.delay));
      return gasGetRequest(action, params, attempt + 1, maxAttempts);
    }
    throw err;
  }
}

async function gasPostRequest(action, payload = {}, attempt = 1, maxAttempts = MAX_ATTEMPTS) {
  let text;
  try {
    const res = await fetch(GAS_URL, {
      method: 'POST',
      mode: 'cors',
      redirect: 'follow',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({ action, ...payload }),
    });
    text = await res.text();
  } catch (networkErr) {
    if (attempt < maxAttempts) {
      await new Promise((r) => setTimeout(r, 300 * attempt));
      return gasPostRequest(action, payload, attempt + 1, maxAttempts);
    }
    throw networkErr;
  }

  try {
    return parseGasResponse(text, attempt, action, maxAttempts);
  } catch (err) {
    if (err?.retry && attempt < maxAttempts) {
      await new Promise((r) => setTimeout(r, err.delay));
      return gasPostRequest(action, payload, attempt + 1, maxAttempts);
    }
    throw err;
  }
}

const PRODUCT_FETCH_TIMEOUT_MS = 20_000;

function withTimeout(promise, ms, label = 'Request') {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out`)), ms);
    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
}

async function gasRequest(action, payload = {}, attempt = 1, options = {}) {
  if (!GAS_URL) {
    throw new Error('Google Apps Script URL is not configured.');
  }

  const skipCache = options.skipCache === true;
  const isProductFetch = action === 'getProducts';
  if (isProductFetch && attempt === 1) {
    if (!skipCache) {
      seedMemoryFromStorage();
      const fresh = memoryCache.data && Date.now() - memoryCache.at < CACHE_TTL_MS;
      if (fresh) return memoryCache.data;
      if (inflight) return inflight;
    } else if (inflightFresh) {
      return inflightFresh;
    }
  }

  const run = (async () => {
    let result;
    if (isProductFetch) {
      const fetchLive = async () => {
        try {
          return await gasGetRequest(
            'getProducts',
            { activeOnly: 'true', _: String(Date.now()) },
            1,
            skipCache ? 2 : 1
          );
        } catch {
          return gasPostRequest(
            'getProducts',
            { activeOnly: true, _: Date.now() },
            1,
            PRODUCT_FETCH_ATTEMPTS
          );
        }
      };
      result = await withTimeout(fetchLive(), PRODUCT_FETCH_TIMEOUT_MS, 'Product fetch');
    } else {
      result = await gasPostRequest(action, payload);
    }

    if (isProductFetch) {
      storeProductCache(result);
    }

    return result;
  })();

  if (isProductFetch && attempt === 1) {
    if (skipCache) inflightFresh = run;
    else inflight = run;
    try {
      return await run;
    } finally {
      if (skipCache) inflightFresh = null;
      else inflight = null;
    }
  }

  return run;
}

export const fetchProducts = (options = {}) =>
  gasRequest('getProducts', { activeOnly: true }, 1, options);

/** Wake GAS + prefetch products so checkout is faster later. */
export function warmStoreBackend() {
  if (!GAS_URL) return;
  fetch(`${GAS_URL}?action=healthCheck`, { method: 'GET', mode: 'cors', redirect: 'follow' }).catch(
    () => {}
  );
  fetch(`${GAS_URL}?action=getProducts&activeOnly=true`, {
    method: 'GET',
    mode: 'cors',
    redirect: 'follow',
  }).catch(() => {});
}

/** Silent prefetch on checkout page. */
export function warmCheckoutBackend() {
  warmStoreBackend();
}

let inflightOrder = null;
let inflightOrderKey = null;

export const ORDER_PENDING_HELP_MESSAGE =
  'Your order may still be processing. Check your email for confirmation — including your spam or junk folder. If you do not receive an email, contact us on WhatsApp with your name and phone number and we will confirm your order for you.';

export class OrderPendingError extends Error {
  constructor(message = ORDER_PENDING_HELP_MESSAGE) {
    super(message);
    this.name = 'OrderPendingError';
  }
}

async function gasPostCreateOrder(payload) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 45000);

  let text;
  try {
    const res = await fetch(GAS_URL, {
      method: 'POST',
      mode: 'cors',
      redirect: 'follow',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({ action: 'createOrder', ...payload }),
      signal: controller.signal,
    });
    text = await res.text();
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new OrderPendingError();
    }
    throw new Error('Could not reach the store. Please check your connection and try again.');
  } finally {
    clearTimeout(timeoutId);
  }

  let data;
  try {
    data = JSON.parse(text);
  } catch {
    const preview = text.replace(/\s+/g, ' ').trim().slice(0, 120);
    const isHtml = preview.startsWith('<!DOCTYPE') || preview.startsWith('<html');
    throw new Error(
      isHtml
        ? 'Something went wrong on our side. Please try again in a moment.'
        : `Invalid response from server (${preview || 'empty'})`
    );
  }

  if (data.error) throw new Error(data.error);
  return data.data !== undefined ? data.data : data;
}

export const submitOrder = async (order) => {
  const key = order.idempotencyKey || '';
  if (inflightOrder && inflightOrderKey === key) {
    return inflightOrder;
  }

  inflightOrderKey = key;
  inflightOrder = (async () => {
    const result = await gasPostCreateOrder(order);
    return normalizeCreateOrderResult(result);
  })();

  try {
    return await inflightOrder;
  } finally {
    inflightOrder = null;
    inflightOrderKey = null;
  }
};

export const fetchOrder = (orderId) => gasRequest('getOrderById', { orderId });

export const submitContactMessage = (message) => gasRequest('saveContact', message);

export const subscribeNewsletter = (email) => gasRequest('subscribeNewsletter', { email });

export const logVisitor = (payload) => gasRequest('logVisitor', payload);
